import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, resolveUrl } from "@/lib/api";
import { MapPin, Calendar, X, Building2 } from "lucide-react";

function Lightbox({ project, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-50/85 backdrop-blur-sm" onClick={onClose} data-testid="project-lightbox">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white border border-slate-300 rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-slate-50/70 text-slate-600 hover:text-slate-900" data-testid="lightbox-close"><X size={20} /></button>
        <div className="h-72 sm:h-96 bg-slate-200 overflow-hidden rounded-t-2xl">
          {project.image_url && <img src={resolveUrl(project.image_url)} alt={project.title} className="w-full h-full object-cover" />}
        </div>
        <div className="p-7">
          <div className="flex flex-wrap items-center gap-3 text-xs font-mono text-brand-400">
            {project.category && <span className="px-2 py-1 rounded-full bg-brand-500/10 border border-brand-500/20">{project.category}</span>}
            {project.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {project.location}</span>}
            {project.year && <span className="inline-flex items-center gap-1"><Calendar size={12} /> {project.year}</span>}
          </div>
          <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold text-slate-900">{project.title}</h2>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">{project.description}</p>
        </div>
      </motion.div>
    </div>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="projects-page">
      <section className="relative py-20 border-b border-slate-200 grain overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1531834685032-c34bf0d84c77?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-50/75" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-xs uppercase tracking-widest font-mono text-brand-400 inline-flex items-center gap-2"><Building2 size={13} /> Our Work</span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold text-slate-900">Projects</h1>
          <p className="mt-4 text-slate-600 max-w-2xl">
            A selection of buildings, infrastructure and cement plants across Iraq powered by HMA chemical solutions and technical support.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        {loading ? (
          <div className="text-center text-slate-500 py-20">Loading projects...</div>
        ) : projects.length === 0 ? (
          <div className="text-center text-slate-500 py-20" data-testid="no-projects">No projects yet.</div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((p, i) => (
              <motion.button
                key={p.id}
                onClick={() => setSelected(p)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (i % 6) * 0.05 }}
                className="group text-left relative rounded-xl overflow-hidden bg-white border border-slate-200 hover:border-brand-500/50 transition-colors"
                data-testid={`project-card-${i}`}
              >
                <div className="h-60 bg-slate-200 overflow-hidden">
                  {p.image_url && <img src={resolveUrl(p.image_url)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-50 via-slate-50/20 to-transparent opacity-90" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono text-brand-300">
                    {p.category && <span className="px-2 py-0.5 rounded-full bg-slate-50/70 border border-brand-500/30">{p.category}</span>}
                    {p.location && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {p.location}</span>}
                    {p.year && <span>{p.year}</span>}
                  </div>
                  <h3 className="mt-2 font-heading text-lg font-semibold text-slate-900">{p.title}</h3>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </section>

      <AnimatePresence>
        {selected && <Lightbox project={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </div>
  );
}
